import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const Content: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Organizing a tournament for the first time can feel overwhelming — you need to handle
      registrations, brackets, schedules, results, and keep every participant informed. But
      the process is simpler than it looks once you break it into clear steps. This guide
      walks you through everything, from the initial idea to crowning a champion.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Step 1: Define your tournament goals
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Before anything else, answer these four questions:
    </Text>
    {[
      { label: 'What game or sport?', desc: 'The format you choose depends heavily on the activity. Chess, football, and esports each have different traditions and constraints.' },
      { label: 'How many players or teams?', desc: 'A 4-person office ping-pong bracket is very different from a 64-team football league. Your participant count shapes every decision that follows.' },
      { label: 'Casual or competitive?', desc: 'A friendly neighborhood tournament needs different rules and communication than an officially ranked competition.' },
      { label: 'One day or multiple rounds?', desc: 'A single afternoon tournament favors single elimination. A weekly event favors round robin or swiss. Match your format to the time you have.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 2 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 16 }}>
      Step 2: Choose the right format
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      The tournament format determines how matches are played and who wins. The four most
      common formats are:
    </Text>
    {[
      { label: 'Single Elimination', desc: 'Lose once and you\'re out. Fast and dramatic — ideal for one-day events with many players. Every match matters.' },
      { label: 'Double Elimination', desc: 'Two losses before elimination. Fairer than single elimination — a bad first match doesn\'t end your tournament. Best for competitive events where players expect a second chance.' },
      { label: 'Round Robin', desc: 'Everyone plays everyone. Maximum fairness and maximum play time, but requires more time. Best for leagues and small groups (4–10 players).' },
      { label: 'Swiss System', desc: 'Players are paired against others with a similar record each round. No eliminations — everyone plays all rounds. Great for large groups when you can\'t fit a full round robin.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 2 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Not sure which to pick? A common hybrid is to run group-stage round robins first,
      then finish with a single elimination bracket for the top finishers — the best of
      both worlds.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Step 3: Write your rules
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Undefined rules cause disputes. Write them down before the tournament starts — even
      a short, informal document prevents arguments mid-event. Cover at minimum:
    </Text>
    {[
      'Win condition — what does it take to win a match? (best of 1, best of 3, score threshold)',
      'Tiebreakers — how ties are broken in the standings',
      'No-shows — how long to wait before awarding a walkover',
      'Disputes — who makes the final call when players disagree',
      'Eligibility — any restrictions on who can participate',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 8, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Step 4: Set up registration
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Collect participant names before generating the bracket. Decide early whether you
      want open registration (anyone can join up to a cap) or invite-only (you add players
      manually). Set a registration deadline — brackets generated with incomplete rosters
      are messy to fix mid-tournament.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      If you're using Tournaly, you can share an 8-character invite code or a direct link
      so participants join themselves, saving you manual data entry. You can also search
      for registered users by email and add them directly.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Step 5: Seed your bracket
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Seeding determines the initial match-ups. The goal is to prevent the two strongest
      players from meeting in the first round. Common seeding approaches:
    </Text>
    {[
      'Ranked seeding — assign positions based on known skill level or previous results. The #1 and #2 seeds end up on opposite sides of the bracket.',
      'Random seeding — draw names randomly. Simpler, but two top players might meet early.',
      'Group seeding — use your registration data (e.g., a sign-up form with self-reported skill) to create rough tiers, then randomize within each tier.',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Step 6: Generate the bracket and share it
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Once your roster is locked and seeds are set, generate the bracket. Share it with
      participants as early as possible — players need time to coordinate match schedules,
      especially for online or multi-day events. A public link, a PDF printout, or a shared
      screenshot all work. The key is that everyone sees the same bracket before the first
      match starts.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Step 7: Run the tournament
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      On tournament day, your main job as organizer is to keep things moving. Practical tips:
    </Text>
    {[
      'Announce match-ups clearly — don\'t assume players know who they play next. Send reminders.',
      'Enter results promptly — update the bracket as soon as each match finishes so the next round can start.',
      'Handle disputes quickly — having a pre-written rule for the most common situations prevents long arguments.',
      'Communicate delays — if a match is running late, let other participants know so they\'re not waiting around.',
      'Keep scores — even if final standing is win/loss only, recording actual scores adds context and often resolves ties.',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Step 8: Announce results and wrap up
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Once the final match is complete, announce the champion publicly — in a group chat,
      on social media, or within your tournament app. Share the final standings so every
      participant can see where they finished. A small ceremony or acknowledgment goes a
      long way toward making participants want to come back for the next edition.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      After the event, take a few minutes to collect feedback. What worked? What caused
      delays or confusion? A short debrief makes the next tournament significantly smoother.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Common mistakes to avoid
    </Text>
    {[
      { label: 'Starting without a full roster', desc: 'Byes and last-minute additions after the bracket is generated create confusion and imbalance.' },
      { label: 'Unclear rules on day one', desc: 'Every rule that\'s undefined will be tested. Write them down before anyone plays.' },
      { label: 'Choosing the wrong format for your time slot', desc: 'A 16-player round robin with 120 matches doesn\'t fit in a 4-hour afternoon event.' },
      { label: 'Manual bracket tracking in spreadsheets', desc: 'Spreadsheets don\'t validate match results, don\'t auto-advance winners, and are easy to break.' },
      { label: 'No communication channel', desc: 'If players don\'t know when they play or what the bracket looks like, delays multiply.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 2 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Organize your first tournament with Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly handles the bracket generation, result tracking, standings, and participant
      communication in one place. Create a tournament, choose your format (single elimination,
      double elimination, round robin, or swiss), add participants, and generate the bracket
      in seconds. Share the public link so everyone follows along in real time.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      The free plan supports up to 3 active tournaments with 16 participants each — enough
      to run your first event without spending a cent. When you're ready to scale up,
      Tournaly Pro removes all limits and adds PDF export, no ads, and support for all formats.
    </Text>
  </View>
);

const ContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Organizar un torneo por primera vez puede parecer abrumador — tienes que gestionar
      inscripciones, brackets, horarios, resultados y mantener informados a todos los
      participantes. Pero el proceso es más sencillo de lo que parece cuando lo divides
      en pasos claros. Esta guía te acompaña en todo el camino, desde la idea inicial
      hasta coronar a un campeón.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Paso 1: Define el objetivo de tu torneo
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Antes de nada, responde estas cuatro preguntas:
    </Text>
    {[
      { label: '¿Qué deporte o juego?', desc: 'El formato que elijas depende mucho de la actividad. El ajedrez, el fútbol y los esports tienen tradiciones y limitaciones distintas.' },
      { label: '¿Cuántos jugadores o equipos?', desc: 'Un bracket de ping-pong de 4 personas en la oficina es muy diferente a una liga de fútbol de 64 equipos. El número de participantes condiciona todas las decisiones que siguen.' },
      { label: '¿Casual o competitivo?', desc: 'Un torneo vecinal amistoso necesita reglas y comunicación distintas a una competición clasificatoria oficial.' },
      { label: '¿Un día o varias rondas?', desc: 'Un torneo de una tarde favorece la eliminación directa. Un evento semanal favorece el round robin o el sistema suizo. Adapta el formato al tiempo disponible.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 2 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 16 }}>
      Paso 2: Elige el formato adecuado
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      El formato del torneo determina cómo se juegan los partidos y quién gana. Los cuatro
      formatos más habituales son:
    </Text>
    {[
      { label: 'Eliminación simple', desc: 'Pierdes una vez y quedas eliminado. Rápido y emocionante — ideal para eventos de un día con muchos jugadores. Cada partido es crucial.' },
      { label: 'Doble eliminación', desc: 'Dos derrotas antes de quedar eliminado. Más justo que la eliminación simple — un mal primer partido no acaba con tu torneo. Ideal para eventos competitivos donde los jugadores esperan una segunda oportunidad.' },
      { label: 'Round Robin', desc: 'Todos juegan contra todos. Máxima equidad y máximo tiempo de juego, pero requiere más tiempo. Perfecto para ligas y grupos pequeños (4–10 jugadores).' },
      { label: 'Sistema Suizo', desc: 'Los jugadores se emparejan con otros de rendimiento similar cada ronda. Sin eliminaciones — todos juegan todas las rondas. Ideal para grupos grandes cuando no cabe un round robin completo.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 2 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      ¿No sabes cuál elegir? Un híbrido habitual es organizar primero una fase de grupos
      en round robin y luego cerrar con un bracket de eliminación directa para los mejores
      clasificados — lo mejor de ambos mundos.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Paso 3: Redacta las reglas
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Las reglas indefinidas generan disputas. Escríbelas antes de que empiece el torneo —
      incluso un documento breve e informal evita discusiones a mitad del evento. Cubre
      como mínimo:
    </Text>
    {[
      'Condición de victoria — ¿qué hace falta para ganar un partido? (mejor de 1, mejor de 3, umbral de puntuación)',
      'Desempates — cómo se rompen los empates en la clasificación',
      'No presentaciones — cuánto tiempo esperar antes de conceder un walkover',
      'Disputas — quién toma la decisión final cuando los jugadores no se ponen de acuerdo',
      'Elegibilidad — cualquier restricción sobre quién puede participar',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 8, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Paso 4: Gestiona las inscripciones
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Recoge los nombres de los participantes antes de generar el bracket. Decide desde el
      principio si quieres inscripción abierta (cualquiera puede unirse hasta un límite) o
      por invitación (añades los jugadores manualmente). Fija una fecha límite de inscripción
      — los brackets generados con nóminas incompletas son difíciles de corregir a mitad
      del torneo.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Si usas Tournaly, puedes compartir un código de invitación de 8 caracteres o un enlace
      directo para que los participantes se unan ellos mismos, sin que tengas que introducir
      los datos manualmente. También puedes buscar usuarios registrados por email y añadirlos
      directamente.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Paso 5: Siembra el bracket
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      El seeding (siembra) determina los enfrentamientos iniciales. El objetivo es evitar que
      los dos jugadores más fuertes se encuentren en la primera ronda. Enfoques habituales:
    </Text>
    {[
      'Siembra por ranking — asigna posiciones según el nivel conocido o los resultados previos. El cabeza de serie #1 y el #2 quedan en lados opuestos del bracket.',
      'Siembra aleatoria — se sortean los nombres. Más sencillo, pero dos jugadores de primer nivel podrían verse en la primera ronda.',
      'Siembra por grupos — usa los datos de inscripción (p.ej., un formulario con nivel autodeclarado) para crear niveles aproximados y luego aleatorizar dentro de cada nivel.',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Paso 6: Genera el bracket y compártelo
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Una vez que la nómina esté cerrada y los seeds asignados, genera el bracket. Compártelo
      con los participantes lo antes posible — los jugadores necesitan tiempo para coordinar
      sus horarios de partidos, especialmente en eventos online o de varios días. Un enlace
      público, un PDF impreso o una captura de pantalla compartida funcionan igual de bien.
      Lo importante es que todos vean el mismo bracket antes de que empiece el primer partido.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Paso 7: Dirección del torneo
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      El día del torneo, tu trabajo principal como organizador es mantener el ritmo. Consejos prácticos:
    </Text>
    {[
      'Anuncia los enfrentamientos con claridad — no des por hecho que los jugadores saben contra quién juegan. Manda recordatorios.',
      'Introduce los resultados en cuanto terminen — actualiza el bracket tan pronto como acabe cada partido para que pueda empezar la siguiente ronda.',
      'Resuelve las disputas rápido — tener una regla escrita para las situaciones más comunes evita largos debates.',
      'Comunica los retrasos — si un partido se está alargando, avisa a los demás para que no estén esperando sin saber.',
      'Guarda las puntuaciones — aunque la clasificación final sea solo victorias/derrotas, registrar los marcadores reales añade contexto y suele resolver empates.',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Paso 8: Anuncia los resultados y cierra el torneo
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Una vez terminada la final, anuncia al campeón públicamente — en un grupo de chat,
      en redes sociales o dentro de tu app de torneos. Comparte la clasificación final para
      que cada participante vea dónde quedó. Un pequeño reconocimiento hace que los
      participantes quieran volver a la próxima edición.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Tras el evento, dedica unos minutos a recoger feedback. ¿Qué funcionó? ¿Qué causó
      retrasos o confusión? Un breve balance hace que el siguiente torneo sea notablemente
      mejor.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Errores habituales que debes evitar
    </Text>
    {[
      { label: 'Empezar sin la nómina completa', desc: 'Los byes y las incorporaciones de última hora una vez generado el bracket crean confusión y desequilibrios.' },
      { label: 'Reglas poco claras el día uno', desc: 'Toda regla indefinida será puesta a prueba. Escríbelas antes de que nadie juegue.' },
      { label: 'Elegir el formato incorrecto para el tiempo disponible', desc: 'Un round robin de 16 jugadores con 120 partidos no cabe en una tarde de 4 horas.' },
      { label: 'Llevar el bracket a mano en hojas de cálculo', desc: 'Las hojas de cálculo no validan resultados, no avanzan a los ganadores automáticamente y son fáciles de romper.' },
      { label: 'No tener un canal de comunicación', desc: 'Si los jugadores no saben cuándo juegan ni cómo está el bracket, los retrasos se multiplican.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 2 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Organiza tu primer torneo con Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly gestiona la generación del bracket, el seguimiento de resultados, la clasificación
      y la comunicación con los participantes en un solo lugar. Crea un torneo, elige tu formato
      (eliminación simple, doble eliminación, round robin o suizo), añade participantes y genera
      el bracket en segundos. Comparte el enlace público para que todos sigan el torneo en
      tiempo real.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      El plan gratuito permite hasta 3 torneos activos con 16 participantes cada uno — suficiente
      para organizar tu primer evento sin gastar un euro. Cuando estés listo para crecer,
      Tournaly Pro elimina todos los límites y añade exportación PDF, sin anuncios y con
      soporte para todos los formatos.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-04-02-how-to-organize-a-tournament',
  title: 'How to Organize a Tournament from Scratch: A Step-by-Step Guide',
  titleEs: 'Cómo Organizar un Torneo desde Cero: Guía Paso a Paso',
  date: '2026-04-02',
  summary: 'A complete guide to organizing any tournament — from choosing the right format and managing registrations to running the bracket and announcing the champion. Everything you need to run your first event.',
  summaryEs: 'Una guía completa para organizar cualquier torneo — desde elegir el formato adecuado y gestionar las inscripciones hasta llevar el bracket y anunciar al campeón. Todo lo que necesitas para tu primer evento.',
  tags: ['Guide', 'Tournament', 'Organizer'],
  Content,
  ContentEs,
};
